import React, { useState, useRef, useEffect } from 'react';
import { Send, Lightbulb } from 'lucide-react';
import Message from './Message';
import { VERDICTS } from '../constants';
import useGreatInsightAnimation from '../hooks/useGreatInsightAnimation';

const InsightVibeCheck = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState('');
  const [showEmailTooltip, setShowEmailTooltip] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [isClosingHowItWorks, setIsClosingHowItWorks] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [isClosingTerms, setIsClosingTerms] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { handleGreatInsightAnimation } = useGreatInsightAnimation();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
    // Focus input on initial load
    inputRef.current?.focus();
    
    // Check if this is the user's first visit
    const hasVisitedBefore = localStorage.getItem('hasVisitedInsightVibeCheck');
    if (!hasVisitedBefore) {
      // Show the "How It Works" modal after a short delay
      setTimeout(() => {
        setShowHowItWorks(true);
        // Mark that they've visited the site
        localStorage.setItem('hasVisitedInsightVibeCheck', 'true');
      }, 800); // Delay to allow page to load first
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Function to call Claude API via backend
  const callClaudeAPI = async (text, isFollowUp = false) => {
    try {
      setIsTyping(true);

      // Prepare API request with conversation history for follow-up questions
      const requestBody = {
        insight: text,
        isFollowUp: isFollowUp,
      };
      
      // If it's a follow-up, include previous messages
      if (isFollowUp) {
        requestBody.previousMessages = messages;
      }

      // Get API URL from environment variable with fallback
      const API_URL = process.env.REACT_APP_API_URL || 'https://insightvibecheck-backend.onrender.com';
      
      // Make API call to your backend endpoint
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // Add conversation ID to headers if it exists
      if (conversationId) {
        headers['x-conversation-id'] = conversationId;
      }
      
      const response = await fetch(`${API_URL}/api/evaluate-insight`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      });

      // Handle non-ok responses by extracting error information
      if (!response.ok) {
        const errorData = await response.json().catch(e => ({}));
        
        // Check if this is an overloaded error
        if (errorData.isOverloaded || 
            errorData.error === 'Server capacity reached' ||
            JSON.stringify(errorData).toLowerCase().includes('overload') ||
            JSON.stringify(errorData).toLowerCase().includes('capacity')) {
          
          const error = new Error('Server capacity reached');
          error.responseData = errorData;
          error.isOverloaded = true;
          throw error;
        }
        
        throw new Error('Failed to get response from API');
      }

      const data = await response.json();
      setIsTyping(false);
      
      // Store the conversation ID if received from backend
      if (data.conversationId && !conversationId) {
        setConversationId(data.conversationId);
      }

      return {
        text: data.response,
        verdict: data.verdict,
        isFollowUp: data.isFollowUp,
        conversationId: data.conversationId,
        modelUsed: data.modelUsed || 'claude-sonnet-4-6' // Default to primary model if not specified
      };
    } catch (error) {
      console.error('Error calling Claude API:', error);
      setIsTyping(false);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    // Determine if this is a follow-up question
    const isFollowUp = messages.length > 0;
    
    const userMessage = { id: Date.now(), text: inputValue, isUser: true };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await callClaudeAPI(inputValue, isFollowUp);
      
      // Only assign a verdict if the backend provided one
      // For "No Verdict" responses, this will be null
      const verdict = response.verdict;
      
      // Add message to state
      setMessages(prev => [...prev, { 
        id: Date.now(), 
        text: response.text, 
        isUser: false, 
        verdict: verdict, // This can be null for "No Verdict" responses
        isFollowUp: response.isFollowUp,
        modelUsed: response.modelUsed // Store the model used
      }]);
      
      // Trigger celebration animation for Great Insights
      handleGreatInsightAnimation(verdict);
    } catch (error) {

      // Check if this is an overloaded error with improved detection
      let isOverloaded = false;
      
      try {
        // First try response data if it exists
        if (error.response && error.response.data) {
          console.log('Error response data:', error.response.data);
          isOverloaded = error.response.data.isOverloaded === true;
        }
        
        // Then try parsing error message as JSON if possible
        if (!isOverloaded && typeof error.message === 'string') {
          if (error.message.includes('{')) {
            try {
              const jsonStr = error.message.substring(error.message.indexOf('{'));
              const responseData = JSON.parse(jsonStr);
              isOverloaded = responseData?.isOverloaded === true || 
                           responseData?.error === 'Server capacity reached';
            } catch (parseError) {
              // Silent fail - couldn't parse JSON from error message
            }
          }
        }
        
        // If still not detected, check the raw error string
        if (!isOverloaded) {
          const errorString = String(error).toLowerCase();
          isOverloaded = errorString.includes('overloaded') || 
                        errorString.includes('capacity') ||
                        errorString.includes('server capacity reached');
        }
      } catch (e) {
        console.error('Error in error handling:', e);
      }
      
      setMessages(prev => [...prev, { 
        id: Date.now(), 
        text: isOverloaded
          ? "We're experiencing high demand right now. Please try again in a few moments." 
          : "Sorry, I couldn't process your message. Please try again.", 
        isUser: false 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Clear all messages and focus input - can be triggered by a button if needed
  const clearMessages = (withAnimation = false) => {
    if (withAnimation) {
      // Create a fade-out effect for existing messages
      const messagesContainer = document.querySelector('.scrollbar-thin');
      if (messagesContainer) {
        // First add a class to start the transition
        messagesContainer.classList.add('opacity-0', 'transition-opacity', 'duration-500');
        
        // Wait for transition to complete, then clear messages
        setTimeout(() => {
          // Clear messages
          setMessages([]);
          // Reset conversation ID
          setConversationId('');
          
          // Remove the transition classes after a short delay and show container again
          setTimeout(() => {
            messagesContainer.classList.remove('opacity-0', 'transition-opacity', 'duration-500');
            messagesContainer.classList.add('animate-fadeIn');
            
            // Remove animation class after it completes
            setTimeout(() => {
              messagesContainer.classList.remove('animate-fadeIn');
            }, 500);
            
            // Focus input field after transition is complete
            inputRef.current?.focus();
          }, 50);
        }, 400);
      }
    } else {
      // Simple reset without animation
      setMessages([]);
      setConversationId('');
      inputRef.current?.focus();
    }
  };
  
  // Handle contact link click
  const handleContactClick = (e) => {
    e.preventDefault(); // Prevent the default mailto behavior
    
    // Copy email to clipboard
    navigator.clipboard.writeText('contact@isthataninsight.com').then(() => {
      setEmailCopied(true);
      
      // Reset the copied state after 3 seconds
      setTimeout(() => {
        setEmailCopied(false);
      }, 3000);
    }).catch(err => {
      console.error('Could not copy email: ', err);
    });
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col">
      {/* Background without cursor effect */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-gray-900 to-gray-800">
        {/* Static background decorative elements */}
        <div className="absolute top-20 left-20 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-orange-600/5 rounded-full blur-3xl"></div>
      </div>
      
      {/* Content container that sits on top of the background */}
      <div className="flex flex-col items-center justify-center flex-grow p-4 relative z-10">
      
      {/* Title above the container with animation */}
      <div className="text-center mb-6 animate-fadeIn">
        <div className="flex items-center justify-center gap-3">
          <span className="text-orange-400 animate-pulse filter drop-shadow-md" 
                style={{filter: 'drop-shadow(0 0 4px rgba(251, 146, 60, 0.4))'}}>
            <Lightbulb size={28} />
          </span>
          <h1 
            className="text-4xl font-medium tracking-wide relative cursor-pointer" 
            style={{
              fontFamily: "'Copernicus Book', Georgia, serif",
              textShadow: '0 2px 10px rgba(255, 255, 255, 0.15)'
            }}
            onClick={() => {
              // Call the animation-enhanced clear messages function
              clearMessages(true);
              
              // Add a glow effect to the title
              const headlineElement = document.querySelector('h1');
              const lightbulbElement = document.querySelector('.text-orange-400');
              const messagesArea = document.querySelector('.overflow-y-auto');
              
              // Add smooth visual reset effect to the messages container
              if (messagesArea && messagesArea.children.length > 0) {
                messagesArea.classList.add('animate-reset');
                setTimeout(() => {
                  messagesArea.classList.remove('animate-reset');
                }, 800);
              }
              
              // Add animations to both title and lightbulb icon
              if (headlineElement && lightbulbElement) {
                // Enhance the lightbulb with glow and scale effect
                lightbulbElement.classList.remove('animate-pulse');
                lightbulbElement.classList.add('scale-125', 'transition-all', 'duration-700', 'animate-glow');
                
                // Enhance the title text with a glow effect
                const spanElement = headlineElement.querySelector('span');
                if (spanElement) {
                  // Add transition for smooth effect
                  spanElement.classList.add('transition-all', 'duration-700');
                  // Create a more pronounced glow
                  spanElement.style.textShadow = '0 0 20px rgba(251, 146, 60, 0.6), 0 0 30px rgba(251, 146, 60, 0.3)';
                  // Add subtle scale effect
                  spanElement.style.transform = 'scale(1.03)';
                }
                
                // Reset styles after animation completes
                setTimeout(() => {
                  // Remove special effects from lightbulb
                  lightbulbElement.classList.remove('scale-125', 'transition-all', 'duration-700', 'animate-glow');
                  lightbulbElement.classList.add('animate-pulse');
                  
                  // Reset title text
                  if (spanElement) {
                    spanElement.classList.remove('transition-all', 'duration-700');
                    spanElement.style.textShadow = '0 2px 10px rgba(255, 255, 255, 0.15)';
                    spanElement.style.transform = '';
                  }
                }, 700);
              }
            }}
            title="Click to start a new conversation"
            onMouseMove={(e) => {
              const headlineElement = e.currentTarget;
              const rect = headlineElement.getBoundingClientRect();
              const x = e.clientX - rect.left; // x position within the element
              const y = e.clientY - rect.top;  // y position within the element

              // Update the gradient position to follow the cursor with more intense colors
              const spanElement = headlineElement.querySelector('span');
              if (spanElement) {
                spanElement.style.color = 'transparent'; // Set to transparent when showing gradient
                spanElement.style.background = `
                  radial-gradient(
                    circle 100px at ${x}px ${y}px, 
                    rgba(251, 146, 60, 0.95), 
                    rgba(251, 146, 60, 0.6) 30%, 
                    rgba(251, 146, 60, 0.3) 60%, 
                    transparent 80%
                  ), 
                  linear-gradient(to right, #f3f4f6, #f3f4f6)
                `;
                spanElement.style.backgroundClip = 'text';
                spanElement.style.webkitBackgroundClip = 'text';
                // Add a subtle text shadow for extra emphasis
                spanElement.style.textShadow = '0 0 8px rgba(251, 146, 60, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              // Reset to solid white when mouse leaves
              const spanElement = e.currentTarget.querySelector('span');
              if (spanElement) {
                spanElement.style.background = '';
                spanElement.style.backgroundClip = '';
                spanElement.style.webkitBackgroundClip = '';
                spanElement.style.color = '#f3f4f6'; // text-gray-100
                spanElement.style.textShadow = '0 2px 10px rgba(255, 255, 255, 0.15)';
              }
            }}
          >
            <span className="text-gray-100" style={{ textShadow: '0 2px 10px rgba(255, 255, 255, 0.15)' }}>
              Insight Vibe Check
            </span>
          </h1>
        </div>
      </div>

      {/* Main Container */}
      <div className="w-full max-w-xl bg-gray-800/90 backdrop-blur-sm rounded-lg shadow-2xl overflow-hidden flex flex-col border border-gray-700 relative z-20" 
           style={{animation: 'slideInFromTop 0.5s ease-out 0.2s both', maxHeight: '85vh'}}>
        {/* Subtle glow effect on the container */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-orange-600/10 pointer-events-none"></div>

        {/* Empty header space - providing a bit of padding */}
        <div className="pt-3 pb-1"></div>

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto px-4 py-2 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent" style={{ height: "300px" }}>
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full space-y-3">
              <div className="w-12 h-12 rounded-full bg-gray-700/50 flex items-center justify-center overflow-hidden border border-orange-500/20">
                <img 
                  src="/ai-avatar.png" 
                  alt="AI Avatar" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to sparkles if image fails to load
                    e.target.style.display = 'none';
                    e.target.parentNode.appendChild(document.createElement('div')).innerHTML = 
                      `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-orange-400">
                        <path d="M12 3V3.75M18.3955 5.6045L17.8695 6.1305M21 12H20.25M18.3955 18.3955L17.8695 17.8695M12 20.25V21M5.6045 18.3955L6.1305 17.8695M3.75 12H3M5.6045 5.6045L6.1305 6.1305M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>`;
                  }}
                />
              </div>
              <p className="text-center text-gray-400 text-sm" style={{fontFamily: "'Inter', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif"}}>Share an advertising insight for measurement</p>
              <div className="text-center text-gray-500 text-xs max-w-xs">
                <p>Because not all insights are created equal</p>
              </div>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <Message key={message.id} message={message} isUser={message.isUser} />
              ))}
              {isTyping && (
                <div className="flex items-center gap-2 text-gray-400 ml-7 mb-2">
                  <div className="flex space-x-1">
                    <div className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                  <span className="text-xs">Evaluating insight...</span>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input form */}
        <div className="border-t border-gray-700/50 p-3">
          <form onSubmit={handleSubmit} className="relative">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={messages.length > 0 ? "Ask a follow-up question or enter a new insight..." : "Enter an insight to evaluate..."}
              className="w-full bg-gray-700/50 border border-gray-600/50 rounded-md px-3 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent text-white text-sm transition-all placeholder-gray-500"
              style={{fontFamily: "'Inter', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif"}}
            />
            <button
              type="submit"
              disabled={isLoading || !inputValue.trim()}
              className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-blue-400 transition-colors disabled:opacity-50 disabled:hover:text-gray-400"
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Send size={16} className="hover:scale-110 transition-transform" />
              )}
            </button>
          </form>
        </div>
      </div>

      </div>
      
      {/* Contact link at the very bottom */}
      <div className="w-full text-center py-2 mt-auto relative z-10">
        <div className="flex items-center justify-center gap-6">
          <div className="relative inline-block">
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a 
              href="#" 
              className="text-gray-500 text-xs hover:text-white transition-colors duration-200"
              onClick={(e) => {
                e.preventDefault();
                setIsClosingHowItWorks(false);
                setShowHowItWorks(true);
              }}
            >
              How does this work?
            </a>
          </div>
          <div className="relative inline-block">
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a 
              href="#" 
              className="text-gray-500 text-xs hover:text-white transition-colors duration-200"
              onClick={(e) => {
                e.preventDefault();
                setIsClosingTerms(false);
                setShowTerms(true);
              }}
            >
              Terms of Service
            </a>
          </div>
          <div className="relative inline-block">
            <a 
              href="mailto:contact@isthataninsight.com" 
              className="text-gray-500 text-xs hover:text-white transition-colors duration-200"
              onClick={handleContactClick}
              onMouseEnter={() => setShowEmailTooltip(true)}
              onMouseLeave={() => setShowEmailTooltip(false)}
            >
              Contact
            </a>
          
            {showEmailTooltip && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-700 text-white text-xs rounded shadow-lg whitespace-nowrap animate-fadeIn">
                {emailCopied ? (
                  <span className="flex items-center gap-1">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Email address copied to clipboard
                </span>
                ) : (
                  <span>Click to copy: contact@isthataninsight.com</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* How It Works Modal */}
      {showHowItWorks && (
        <div 
          className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 ${isClosingHowItWorks ? 'animate-fadeOut' : 'animate-fadeIn'}`}
          onClick={() => {
            setIsClosingHowItWorks(true);
            setTimeout(() => {
              setShowHowItWorks(false);
              setIsClosingHowItWorks(false);
            }, 300);
          }}
        >
          <div 
            className={`bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto ${isClosingHowItWorks ? 'animate-fadeOut' : 'animate-fadeIn'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-start">
                <h2 className="mb-5 flex items-center gap-3">
                  <span className="filter" style={{filter: 'drop-shadow(0 0 4px rgba(251, 146, 60, 0.4))'}}>
                    <Lightbulb size={26} className="text-orange-400" />
                  </span>
                  <span className="text-2xl md:text-2xl lg:text-3xl text-white" style={{ 
                    fontFamily: "'Copernicus Book', Georgia, serif",
                    letterSpacing: '0.02em',
                    lineHeight: '1.2'
                  }}>
                    Your Guide to Insight Excellence
                  </span>
                </h2>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsClosingHowItWorks(true);
                    setTimeout(() => {
                      setShowHowItWorks(false);
                      setIsClosingHowItWorks(false);
                    }, 300);
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4 text-gray-200 text-sm">
                <p>
                  Insight Vibe Check helps you evaluate advertising insights using advanced AI.
                </p>
                
                <p>Every insight receives one of three verdicts:</p>
                
                <div className="flex flex-col gap-4 mt-6 mb-4">
                  {/* Great Insight Example */}
                  <div className="rounded-lg border border-gray-700/50 bg-gray-700/30 p-4">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-400/40 shadow-inner shadow-amber-400/10 animate-shimmer">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L14.4 9.2H22L16 13.8L18.4 21L12 16.4L5.6 21L8 13.8L2 9.2H9.6L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Great Insight!
                      </span>
                    </div>
                    <p className="mt-2 text-gray-300 text-xs">
                      These are the cream of the crop. Top-tier insights that demonstrate exceptional strategic thinking and originality, either revealing new opportunities or meaningfully challenging conventional thinking. If you nail these qualities, your insight will be rewarded with the elusive golden buzzer.
                    </p>
                  </div>
                  
                  {/* Good Insight Example */}
                  <div className="rounded-lg border border-gray-700/50 bg-gray-700/30 p-4">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-300 border border-green-500/30">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Good Insight
                      </span>
                    </div>
                    <p className="mt-2 text-gray-300 text-xs">
                      These are solid insights that provide meaningful value and hint at something true. They demonstrate good analytical thinking and can effectively guide marketing strategies. They're good - but not great.
                    </p>
                  </div>
                  
                  {/* Needs Improvement Example */}
                  <div className="rounded-lg border border-gray-700/50 bg-gray-700/30 p-4">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-500/20 text-red-300 border border-red-500/30">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Needs Improvement
                      </span>
                    </div>
                    <p className="mt-2 text-gray-300 text-xs">
                      Insights that require additional development to reach their potential. These may be too obvious, lack evidence, miss strategic connections, or need clearer action steps to be truly valuable.
                    </p>
                  </div>
                </div>
                
                <p>After receiving your initial verdict, you can ask follow-up questions to understand how to improve your insights or explore related ideas.</p>
                
                <p className="italic text-gray-400 mt-4 text-[10px]">This tool evaluates the quality of insights based on general advertising principles. While it identifies strong versus weak insights, it cannot determine if an insight is the right fit for your specific client, campaign, or strategic goals. Always consider your client's unique needs when applying these evaluations.</p>
              </div>
              
            </div>
          </div>
        </div>
      )}
      
      {/* Terms of Service Modal */}
      {showTerms && (
        <div 
          className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 ${isClosingTerms ? 'animate-fadeOut' : 'animate-fadeIn'}`}
          onClick={() => {
            setIsClosingTerms(true);
            setTimeout(() => {
              setShowTerms(false);
              setIsClosingTerms(false);
            }, 300);
          }}
        >
          <div 
            className={`bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent ${isClosingTerms ? 'animate-fadeOut' : 'animate-fadeIn'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-start">
                <h2 className="mb-5 flex items-center gap-3">
                  <span className="filter" style={{filter: 'drop-shadow(0 0 4px rgba(251, 146, 60, 0.4))'}}>
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-orange-400">
                      <path d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 14H8.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 14H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 14H16.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 18H8.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 18H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 18H16.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <span className="text-2xl md:text-2xl lg:text-3xl text-white" style={{ 
                    fontFamily: "'Copernicus Book', Georgia, serif",
                    letterSpacing: '0.02em',
                    lineHeight: '1.2'
                  }}>
                    The Fine Print
                  </span>
                </h2>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsClosingTerms(true);
                    setTimeout(() => {
                      setShowTerms(false);
                      setIsClosingTerms(false);
                    }, 300);
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4 text-gray-200 text-sm">
                <div className="bg-orange-900/20 p-4 rounded-md border border-orange-800/30">
                  <h3 className="font-medium text-orange-400 mb-3 text-lg">TL;DR</h3>
                  <ul className="list-disc pl-5 space-y-2 text-sm">
                    <li><span className="font-medium text-white">Your Content Remains Yours:</span> You retain full ownership of all advertising insights uploaded. We do not collect or store the advertising insights you submit.</li>
                    <li><span className="font-medium text-white">Third-Party Processing:</span> Your content is transmitted to third-party AI providers governed by their own terms. Who knows what shennanigans they get up to, but it's probably unlikely they care much about your advertising insight.</li>
                    <li><span className="font-medium text-white">No Guarantees:</span> AI-generated outputs are provided "as is" without guarantees of accuracy.</li>
                  </ul>
                </div>
                
                <h3 className="font-medium text-white mt-6 text-sm">Terms of Service & Privacy Policy</h3>
                
                <h4 className="font-medium text-white text-xs underline">Introduction</h4>
                <p className="text-xs">
                  Welcome to isthataninsight.com (the "Site"). By accessing or using the Site, you agree to these Terms of Service & Privacy Policy ("Terms"). If you do not agree, please do not use the Site.
                </p>
                
                <h4 className="font-medium text-white mt-4 text-xs underline">Service Description</h4>
                <p className="text-xs">
                  The Site offers AI feedback on advertising insights. Your content is sent to third-party AI providers whose own terms govern how they handle your data. While we only transmit your content to provide the service, we cannot control third parties' use of your information.
                </p>
                
                <h4 className="font-medium text-white mt-4 text-xs underline">Data Collection & Privacy</h4>
                <p className="text-xs">
                  We collect only anonymous usage metrics (number of conversations and messages per week) and use functional cookies for preferences. No third-party analytics or personal data collection. GDPR/CCPA users: we collect no identifiable personal information.
                </p>
                
                <h4 className="font-medium text-white mt-4 text-xs underline">Intellectual Property</h4>
                <p className="text-xs">
                  All content, features, and functionality on the Site (except for user-submitted content) are the exclusive property of isthataninsight.com and are protected by applicable intellectual property laws.
                </p>
                
                <h4 className="font-medium text-white mt-4 text-xs underline">Disclaimer of Warranties & AI-Generated Outputs</h4>
                <p className="text-xs">
                  The Site and its services are provided "as is" without warranties of any kind, express or implied. AI-generated outputs are for informational purposes only and may be inaccurate or incomplete. You use the service at your own risk.
                </p>
                
                <h4 className="font-medium text-white mt-4 text-xs underline">Limitation of Liability</h4>
                <p className="text-xs">
                  To the fullest extent permitted by law, our total liability (and that of our affiliates) for any claim arising from your use of the Site is limited to $100. We are not liable for any indirect, incidental, special, or consequential damages.
                </p>
                
                <h4 className="font-medium text-white mt-4 text-xs underline">Indemnification</h4>
                <p className="text-xs">
                  You agree to indemnify and hold harmless isthataninsight.com, its owners, and affiliates from any claims, damages, or expenses arising out of your use of the Site or violation of these Terms.
                </p>
                
                <h4 className="font-medium text-white mt-4 text-xs underline">Export and Legal Restrictions</h4>
                <p className="text-xs">
                  Access to the Site from countries subject to U.S. export restrictions is prohibited. You are responsible for complying with any local laws governing your use of the service.
                </p>
                
                <h4 className="font-medium text-white mt-4 text-xs underline">Changes to These Terms</h4>
                <p className="text-xs">
                  We reserve the right to update these Terms at any time. The "Last Updated" date will reflect any changes. Continued use of the Site after such changes constitutes your acceptance of the revised Terms.
                </p>
                
                <h4 className="font-medium text-white mt-4 text-xs underline">Governing Law & Dispute Resolution</h4>
                <p className="text-xs">
                  These Terms are governed by the laws of the State of New York. Any disputes arising from these Terms will be resolved in the courts located in New York, New York.
                </p>
                
                <h4 className="font-medium text-white mt-4 text-xs underline">Contact Information</h4>
                <p className="text-xs">
                  If you have any questions about these Terms, please contact us at:<br />
                  Email: contact@isthataninsight.com
                </p>
                
                <p className="italic text-gray-400 mt-6 text-xs">Last updated: March 2025</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsightVibeCheck;